import { GameLobbyMode, User } from "./types";

/**
 * Host serving the poker client. One constant so a redeploy, or pointing at a
 * local vite server while testing a branch, is a one-line change.
 */
export const CLIENT_BASE_URL = "https://poker-test-web.gamecloud.one";

export const DEFAULT_OPERATOR_ID = 91;

export const CLIENT_SCRIPT_ID = "pokerClientScript";

/**
 * Bet structures offered in the launcher. HYBRID_LIMIT is left out because the
 * server only ever pairs it with TENNESSEE_HOLDEM, which is not offered below.
 * No combination of it with the variants below can match a table, and an
 * unmatched game leaves the lobby loading forever.
 */
export const BET_STRATEGIES = [
  "NO_LIMIT",
  "POT_LIMIT",
  "FIXED_LIMIT",
  "ALL_IN_OR_FOLD",
] as const;

/**
 * Variants offered in the launcher. The client keeps no variant list of its own
 * (poker ce7e87327d): it launches any variant it is given, and the server
 * decides which have tables. A variant the server offers nothing for leaves the
 * lobby loading forever, so only add one here once it has tables to match.
 */
export const VARIANTS = [
  "TEXAS_HOLDEM",
  "OMAHA",
  "OMAHA_HILO",
  "TONGITS",
] as const;

export type Variant = (typeof VARIANTS)[number];

export const isVariant = (value: string): value is Variant =>
  (VARIANTS as readonly string[]).includes(value);

/**
 * Tongits has no betting. The backoffice hides its bet structure and stores
 * FIXED_LIMIT behind it, so an id naming any structure would match no table;
 * the client takes "TONGITS-<mode>" instead (poker 91f0edff84).
 */
export const hasBetStructure = (variant: Variant): boolean =>
  variant !== "TONGITS";

export const MODES = ["cash", "spin", "sitandgo"] as const;

export const DEFAULT_BET_STRATEGY = "NO_LIMIT";
export const DEFAULT_VARIANT: Variant = "TEXAS_HOLDEM";
export const DEFAULT_MODE = "cash";

/**
 * The client reads one id rather than a game and a mode, so an operator cannot
 * pair the two wrongly. Bet structures, variants and modes spell their own words
 * with underscores, which leaves the dashes to separate the parts:
 * "NO_LIMIT-TEXAS_HOLDEM-cash", or "TONGITS-cash" for a game without betting.
 */
export const gameId = (
  betStrategy: string,
  variant: Variant,
  mode: GameLobbyMode,
): string =>
  hasBetStructure(variant)
    ? `${betStrategy}-${variant}-${mode}`
    : `${variant}-${mode}`;

export const DEFAULT_GAME_ID = gameId(
  DEFAULT_BET_STRATEGY,
  DEFAULT_VARIANT,
  DEFAULT_MODE,
);

/**
 * The options the poker client reads, built once and shared by all three launch
 * surfaces: the iframe URL, the web component's script URL and its params
 * attribute. Building them in one place keeps the three from drifting apart —
 * the params attribute overrides the script URL query inside the element, so a
 * mismatch silently wins in one place and loses in another.
 */
export const clientParams = (user: User): URLSearchParams => {
  const params = new URLSearchParams();
  if (user.gameLobby) {
    params.set("lobby", "game");
    params.set("gameId", user.gameId ?? DEFAULT_GAME_ID);
  }
  if (user.featureFlags) {
    params.set("featureFlags", user.featureFlags);
  }
  return params;
};

/**
 * The iframe target. game-lobby.html rather than index.html?lobby=game: its boot
 * forces the game lobby, so a dropped lobby param cannot silently land you in the
 * classic client, and it ships the slim DOM the Pixi lobby expects.
 */
export const iframeUrl = (user: User, token: string): string => {
  const params = clientParams(user);
  params.set("operator", String(user.operatorId));
  params.set("token", token);
  const page = user.gameLobby ? "/game-lobby.html" : "/";
  return `${CLIENT_BASE_URL}${page}?${params.toString()}`;
};

/**
 * The web component's script URL. The lobby options have to be here and not only
 * on the element: the element module prewarms on its own script URL at module
 * load, before any attribute is read, and the first prewarm fixes the preload
 * profile. Without them the game lobby downloads the classic profile.
 */
export const elementScriptUrl = (user: User): string => {
  const params = clientParams(user);
  params.set("operator", String(user.operatorId));
  return `${CLIENT_BASE_URL}/poker-client-element.js?${params.toString()}`;
};

/**
 * The client keeps its session in a cookie shared by every tab, and a client
 * booted without a token resumes it. The preload runs before anyone logs in
 * here, so a session left by another tab must not be picked up.
 */
export const forgetClientSession = () => {
  document.cookie = "poker-client_pokerSession=; Max-Age=0; path=/";
};

/** The client is preloaded for this user at page load, before anyone logs in. */
export const preloadUser = (): User => ({
  loggedIn: false,
  preload: true,
  webComponent: true,
  operatorId: DEFAULT_OPERATOR_ID,
});

/**
 * Whether the already loaded client script, if any, can serve this user. The
 * element module is evaluated once per document with the options baked into its
 * script URL, so anything else needs a fresh page.
 */
export const loadedClientMatches = (user: User): boolean => {
  const script = document.querySelector<HTMLScriptElement>(
    `#${CLIENT_SCRIPT_ID}`,
  );
  if (script == null) {
    return true;
  }
  return Boolean(user.webComponent) && script.src === elementScriptUrl(user);
};
